export type EventManagerTranslations = {
  assignedParticipants: {
    collectionName: string;
    singularName: string;
    description: string;
    fields: {
      number: string;
      numberDescription: string;
      team: string;
      teamDescription: string;
    };
  };
  participantEconomy: {
    entityAction: string;
    ariaLabel: string;
    title: string;
    description: string;
    close: string;
    empty: string;
    error: string;
    scoresSection: string;
    coinsSection: string;
    individualScore: string;
    teamScore: string;
    coinsBalance: string;
    missingDoc: string;
    neverUpdated: string;
    ledgerCollectionName: string;
    ledgerSingularName: string;
    ledgerDescription: string;
    ledgerCreatedAt: string;
    ledgerType: string;
    ledgerCoinsDelta: string;
    ledgerLevel: string;
    ledgerTypeLevelReward: string;
    ledgerTypeSpend: string;
    ledgerTypeAdjustment: string;
  };
  eventInvitations: {
    collectionName: string;
    singularName: string;
    description: string;
    fields: {
      email: string;
      emailDescription: string;
      role: string;
      status: string;
      invitedBy: string;
      emailSentAt: string;
      emailNotSent: string;
      acceptedAt: string;
      acceptedBy: string;
    };
    roles: {
      participant: string;
      operator: string;
      presenter: string;
      manager: string;
    };
    status: {
      pending: string;
      accepted: string;
    };
  };
  assignmentMode: {
    name: string;
    description: string;
    forEveryone: string;
    invitationOnly: string;
    approvalOnly: string;
  };
  eventStatus: {
    provisioning: string;
    provisioning_failed: string;
    running: string;
    archived: string;
    deleting: string;
    denialProvisioningFailedWithDetail: string;
    denialProvisioningFailed: string;
    denialProvisioning: string;
    denialArchived: string;
    denialGeneric: string;
  };
  eventActions: {
    statusUpdated: string;
    statusUpdateFailed: string;
    republishName: string;
    republishTitle: string;
    republishBody: string;
    republishSuccess: string;
    republishQueued: string;
    unarchiveName: string;
    unarchiveTitle: string;
    unarchiveBody: string;
    archiveName: string;
    archiveTitle: string;
    archiveBody: string;
    deleteName: string;
  };
  eventAccess: {
    managerDenied: string;
    accessDeniedTitle: string;
    provisioningDeniedTitle: string;
    backToAccount: string;
  };
  deleteEvent: {
    ariaLabel: string;
    title: string;
    description: string;
    confirmLabel: string;
    cancel: string;
    confirm: string;
    queued: string;
  };
  participantProgress: {
    progressCollectionName: string;
    progressSingularName: string;
    progressDescription: string;
    unitProgressCollectionName: string;
    unitProgressSingularName: string;
    unitProgressDescription: string;
    logsCollectionName: string;
    logsSingularName: string;
    logsDescription: string;
    viewTitle: string;
    viewDescription: string;
    selectParticipant: string;
    logsSectionTitle: string;
    searchPlaceholder: string;
    showingLimited: string;
    searchToFindMore: string;
    delayColumn: string;
    durationColumn: string;
  };
  actionTypes: {
    user: {
      avatar_changed: string;
      nick_changed: string;
    };
    level: {
      entered: string;
      exited: string;
    };
    question: {
      answered: string;
    };
    unit: {
      access_unlocked: string;
    };
    event: {
      content_downloaded: string;
      joined: string;
      left: string;
    };
  };
  contentInstalls: {
    viewTitle: string;
    viewDescription: string;
    publishedVersion: string;
    upToDate: string;
    outdated: string;
    notInstalled: string;
    empty: string;
    colParticipant: string;
    colVersion: string;
    colInstalledAt: string;
    colStatus: string;
    statusUpToDate: string;
    statusOutdated: string;
    statusNotInstalled: string;
    searchPlaceholder: string;
    pageStatus: string;
    prevPage: string;
    nextPage: string;
  };
  eventContent: {
    viewTitle: string;
    viewDescription: string;
    empty: string;
    emptyUnpublished: string;
    unlock: string;
    scheduleUnlock: string;
    scheduledFor: string;
    unlocked: string;
    unlockFailed: string;
    scheduleTitle: string;
    scheduleDescription: string;
    scheduleConfirm: string;
    scheduleInvalid: string;
    accessCode: string;
    copyAccessCode: string;
    copiedAccessCode: string;
    unlockMode: string;
    unlockModeDescription: string;
    unlockModeFree: string;
    unlockModeBlocked: string;
    noLevels: string;
    expandUnit: string;
    collapseUnit: string;
    startedCount: string;
    completedCount: string;
    viewStats: string;
    statsTitle: string;
    statsAria: string;
    close: string;
    notStarted: string;
    inProgress: string;
    completed: string;
    percentOfParticipants: string;
    avgStars: string;
    avgScore: string;
    avgDuration: string;
    starDistribution: string;
    starsLabel: string;
    levelNumber: string;
    modalities: {
      quiz: string;
      story: string;
      treasureHunt: string;
      towerClimb: string;
      maze: string;
      reward: string;
    };
    difficulties: {
      normal: string;
      hard: string;
      superHard: string;
    };
    playFormats: {
      solo: string;
      team: string;
    };
    computeStats: string;
    computeStatsConfirmTitle: string;
    computeStatsConfirmBody: string;
    computeStatsQueued: string;
    computeStatsRunning: string;
    computeStatsNever: string;
    computeStatsLastRun: string;
    computeStatsCooldown: string;
    computeStatsFailed: string;
  };
  learningStats: {
    viewTitle: string;
    viewDescription: string;
    empty: string;
    emptyUnpublished: string;
    close: string;
    computeStats: string;
    computeStatsConfirmTitle: string;
    computeStatsConfirmBody: string;
    computeStatsRunning: string;
    computeStatsNever: string;
    computeStatsLastRun: string;
    computeStatsCooldown: string;
    computeStatsFailed: string;
    accuracy: string;
    answered: string;
    weakest: string;
    ofParticipants: string;
    answersCount: string;
    peopleCount: string;
    easy: string;
    medium: string;
    hard: string;
    unitColumn: string;
    noQuestions: string;
    noAnswersInCell: string;
    sampleTooSmall: string;
    questionsTitle: string;
    topWrong: string;
    verdictNever: string;
    verdictNoAnswers: string;
    verdictTooLittle: string;
    verdictGoingWell: string;
    verdictWeakest: string;
  };
  eventStats: {
    viewTitle: string;
    viewDescription: string;
  };
  eventRanking: {
    individualTitle: string;
    individualDescription: string;
    teamTitle: string;
    teamDescription: string;
    refresh: string;
    refreshQueued: string;
    refreshCooldown: string;
    refreshError: string;
    refreshing: string;
    lastUpdated: string;
    lastUpdatedNever: string;
    searchIndividual: string;
    searchTeam: string;
    emptyIndividual: string;
    emptyTeam: string;
    colRank: string;
    colName: string;
    colTeam: string;
    colScore: string;
    colUpdated: string;
    colTeamNumber: string;
    colTeamScore: string;
  };
};

export const eventManagerTranslationsEn: EventManagerTranslations = {
  assignedParticipants: {
    collectionName: "Participants",
    singularName: "Participant",
    description:
      "Players in this session (doc id = Auth uid). Level progress and action logs are subcollections here.",
    fields: {
      number: "Number",
      numberDescription: "Assigned slot number (1, 2, 3…).",
      team: "Team",
      teamDescription: "Participant's team.",
    },
  },
  participantEconomy: {
    entityAction: "View scores & coins",
    ariaLabel: "Participant economy",
    title: "Scores & coins",
    description: "Participant {{uid}}",
    close: "Close",
    empty: "No economy data yet for this participant.",
    error: "Could not load economy: {{message}}",
    scoresSection: "Scores",
    coinsSection: "Coins",
    individualScore: "Individual score",
    teamScore: "Team score",
    coinsBalance: "Balance",
    missingDoc: "Not created yet",
    neverUpdated: "No update timestamp",
    ledgerCollectionName: "Coin movements",
    ledgerSingularName: "Coin movement",
    ledgerDescription: "Coins earned, spent, or adjusted.",
    ledgerCreatedAt: "Created at",
    ledgerType: "Type",
    ledgerCoinsDelta: "Coins (+/−)",
    ledgerLevel: "Level",
    ledgerTypeLevelReward: "Level reward",
    ledgerTypeSpend: "Spend",
    ledgerTypeAdjustment: "Adjustment",
  },
  eventInvitations: {
    collectionName: "Invitations",
    singularName: "Invitation",
    description:
      "Invite someone by email with a role for this session. They receive access when they sign in with that address.",
    fields: {
      email: "Email",
      emailDescription: "They must sign in with this address.",
      role: "Role",
      status: "Status",
      invitedBy: "Invited by",
      emailSentAt: "Email sent",
      emailNotSent: "Not sent",
      acceptedAt: "Accepted at",
      acceptedBy: "Accepted by",
    },
    roles: {
      participant: "Participant",
      operator: "Operator",
      presenter: "Presenter",
      manager: "Manager",
    },
    status: {
      pending: "Pending",
      accepted: "Accepted",
    },
  },
  assignmentMode: {
    name: "Automatic assignment",
    description:
      "Staff invitations apply as soon as they are accepted, in every option.",
    forEveryone: "For everyone",
    invitationOnly: "Invitation only",
    approvalOnly: "Approval only",
  },
  eventStatus: {
    provisioning: "Provisioning",
    provisioning_failed: "Provisioning failed",
    running: "Active",
    archived: "Archived",
    deleting: "Deleting",
    denialProvisioningFailedWithDetail: "Could not provision the event: {{detail}}",
    denialProvisioningFailed:
      "Could not provision the event. Check the status in the events list or contact support.",
    denialProvisioning:
      "The event is being provisioned (slots and teams). Wait until the status is Active to open Event Manager.",
    denialArchived: "This event is archived.",
    denialGeneric: "This event is not available for administration.",
  },
  eventActions: {
    statusUpdated: "Status updated",
    statusUpdateFailed: "Could not update status",
    republishName: "Update content",
    republishTitle: "Update content",
    republishBody:
      "Generate a new content version from the learning path? Participants need connectivity to download it; until then they cannot play with the updated content.",
    republishSuccess: "Content updated",
    republishQueued: "Content update queued",
    unarchiveName: "Unarchive",
    unarchiveTitle: "Unarchive event",
    unarchiveBody:
      "Unarchive this event? It will return to the main list of active events.",
    archiveName: "Archive",
    archiveTitle: "Archive event",
    archiveBody:
      "Archive this event? It will no longer appear in the main list. You can restore it from Archived events.",
    deleteName: "Delete",
  },
  eventAccess: {
    managerDenied:
      "You do not have permission to manage this event. If you need access, ask the account owner for an event or account manager invitation.",
    accessDeniedTitle: "No access to this event",
    provisioningDeniedTitle: "Event being prepared",
    backToAccount: "Back to account panel",
  },
  deleteEvent: {
    ariaLabel: "Delete event",
    title: "Delete event",
    description:
      "You will permanently delete the event and all its history: participants, slots, teams, progress, action records, leaderboards, session content, and related invitations. This cannot be undone.",
    confirmLabel: "To confirm, type exactly:",
    cancel: "Cancel",
    confirm: "Delete event",
    queued: "Deletion queued",
  },
  participantProgress: {
    progressCollectionName: "Progress",
    progressSingularName: "Progress",
    progressDescription: "Participant progress per level.",
    unitProgressCollectionName: "Unit progress",
    unitProgressSingularName: "Unit progress",
    unitProgressDescription: "Participant progress per unit (access unlock + lifecycle).",
    logsCollectionName: "Logs",
    logsSingularName: "Log",
    logsDescription: "Chronological record of participant actions.",
    viewTitle: "Participant progress",
    viewDescription:
      "Select a participant to see progress by unit and logs.",
    selectParticipant: "Select a participant to view progress.",
    logsSectionTitle: "Logs",
    searchPlaceholder: "Search participants",
    showingLimited: "Showing {{shown}} of {{total}} participants.",
    searchToFindMore: "Use search to find others.",
    delayColumn: "Delay",
    durationColumn: "Duration",
  },
  actionTypes: {
    user: {
      avatar_changed: "Avatar changed",
      nick_changed: "Nickname changed",
    },
    level: {
      entered: "Entered level",
      exited: "Exited level",
    },
    question: {
      answered: "Question answered",
    },
    unit: {
      access_unlocked: "Unit access unlocked",
    },
    event: {
      content_downloaded: "Downloaded event content",
      joined: "Joined event",
      left: "Left event",
    },
  },
  contentInstalls: {
    viewTitle: "Content installs",
    viewDescription:
      "Who has the published offline package installed, and when they last updated.",
    publishedVersion: "Published",
    upToDate: "Up to date",
    outdated: "Outdated",
    notInstalled: "Not installed",
    empty: "No participants found.",
    colParticipant: "Participant",
    colVersion: "Installed version",
    colInstalledAt: "Installed at",
    colStatus: "Status",
    statusUpToDate: "Up to date",
    statusOutdated: "Outdated",
    statusNotInstalled: "Not installed",
    searchPlaceholder: "Search participants",
    pageStatus: "Page {{page}} of {{pageCount}} ({{total}})",
    prevPage: "Previous",
    nextPage: "Next",
  },
  eventContent: {
    viewTitle: "Map",
    viewDescription:
      "Units and levels published for this session, with access codes and computed completion stats.",
    empty: "This published version has no units.",
    emptyUnpublished: "No content has been published for this event yet.",
    unlock: "Unlock",
    scheduleUnlock: "Schedule",
    scheduledFor: "Opens {{when}}",
    unlocked: "Unlocked",
    unlockFailed: "Could not unlock this unit.",
    scheduleTitle: "Schedule unlock",
    scheduleDescription:
      "The unit stays closed until this time. Participants can still enter with the access code.",
    scheduleConfirm: "Schedule",
    scheduleInvalid: "Pick a date and time in the future.",
    accessCode: "Access code",
    copyAccessCode: "Copy",
    copiedAccessCode: "Copied",
    unlockMode: "Unlock mode",
    unlockModeDescription:
      "Open when reached: participants enter as soon as they arrive. Locked until opened: stays closed until the event opens it (date, access code, or Unlock in admin).",
    unlockModeFree: "Open when reached",
    unlockModeBlocked: "Locked until opened",
    noLevels: "No levels configured for this unit.",
    expandUnit: "Show levels",
    collapseUnit: "Hide levels",
    startedCount: "{{count}} started ({{percent}}%)",
    completedCount: "{{count}} completed ({{percent}}%)",
    viewStats: "View stats",
    statsTitle: "Level stats",
    statsAria: "Level stats",
    close: "Close",
    notStarted: "Not started",
    inProgress: "In progress",
    completed: "Completed",
    percentOfParticipants: "{{percent}}%",
    avgStars: "Average stars",
    avgScore: "Average score",
    avgDuration: "Average time",
    starDistribution: "Star distribution",
    starsLabel: "{{stars}} stars",
    levelNumber: "Level {{order}}",
    modalities: {
      quiz: "Quiz",
      story: "Story",
      treasureHunt: "Treasure hunt",
      towerClimb: "Tower climb",
      maze: "Maze",
      reward: "Reward",
    },
    difficulties: {
      normal: "Normal",
      hard: "Hard",
      superHard: "Super hard",
    },
    playFormats: {
      solo: "Solo",
      team: "Team",
    },
    computeStats: "Recalculate stats",
    computeStatsConfirmTitle: "Recalculate level stats?",
    computeStatsConfirmBody:
      "This scans every participant's progress and can take several minutes. You cannot run it again for a few minutes.",
    computeStatsQueued: "Stats recompute queued.",
    computeStatsRunning: "Calculating stats…",
    computeStatsNever: "Stats have not been calculated yet.",
    computeStatsLastRun: "Last calculated {{when}}",
    computeStatsCooldown: "Wait {{seconds}} s before calculating again.",
    computeStatsFailed: "Last calculate failed: {{error}}",
  },
  learningStats: {
    viewTitle: "Learning",
    viewDescription:
      "How well participants are answering questions, by unit and difficulty. Every answer counts.",
    empty: "This published version has no units.",
    emptyUnpublished: "No content has been published for this event yet.",
    close: "Close",
    computeStats: "Recalculate stats",
    computeStatsConfirmTitle: "Recalculate learning stats?",
    computeStatsConfirmBody:
      "This scans every participant's answers and can take several minutes. You cannot run it again for a few minutes.",
    computeStatsRunning: "Calculating stats…",
    computeStatsNever: "Stats have not been calculated yet.",
    computeStatsLastRun: "Last calculated {{when}}",
    computeStatsCooldown: "Wait {{seconds}} s before calculating again.",
    computeStatsFailed: "Last calculate failed: {{error}}",
    accuracy: "Accuracy",
    answered: "Answered",
    weakest: "Weakest cell",
    ofParticipants: "Participants who answered / assigned",
    answersCount: "{{count}} answers",
    peopleCount: "{{count}} people",
    easy: "Easy",
    medium: "Medium",
    hard: "Hard",
    unitColumn: "Unit",
    noQuestions: "No questions",
    noAnswersInCell: "No answers in this pool yet.",
    sampleTooSmall: "Fewer than {{min}} answers — not stable yet",
    questionsTitle: "Questions",
    topWrong: "Most missed option: {{option}} ({{count}})",
    verdictNever: "Calculate stats to see how the group is doing on questions.",
    verdictNoAnswers: "Nobody has answered a question yet.",
    verdictTooLittle:
      "There are answers, but not enough in any unit and difficulty to judge.",
    verdictGoingWell: "The group is doing well across units and difficulties.",
    verdictWeakest:
      "Weakest pool: {{unit}} · {{difficulty}} · {{percent}}%.",
  },
  eventStats: {
    viewTitle: "Event stats",
    viewDescription:
      "How many participant slots and teams are registered and active in this session.",
  },
  eventRanking: {
    individualTitle: "Individual ranking",
    individualDescription:
      "Live top 100 from the event leaderboard. Refresh rebuilds ranks from current scores.",
    teamTitle: "Team ranking",
    teamDescription:
      "Live team ranking from the event leaderboard. Refresh rebuilds the individual ranking (team ranks are not rebuilt yet).",
    refresh: "Update ranking",
    refreshQueued: "Ranking update queued.",
    refreshCooldown: "Wait {{seconds}} s before updating again.",
    refreshError: "Could not queue the ranking update.",
    refreshing: "Updating ranking…",
    lastUpdated: "Last updated {{when}}",
    lastUpdatedNever: "Ranking has not been rebuilt yet.",
    searchIndividual: "Search by name or team number",
    searchTeam: "Search by team number",
    emptyIndividual: "No individual ranking yet.",
    emptyTeam: "No team ranking yet.",
    colRank: "#",
    colName: "Name",
    colTeam: "Team",
    colScore: "Score",
    colUpdated: "Updated",
    colTeamNumber: "Team",
    colTeamScore: "Score",
  },
};

export const eventManagerTranslationsEs: EventManagerTranslations = {
  assignedParticipants: {
    collectionName: "Participants",
    singularName: "Participant",
    description:
      "Jugadores en esta sesión (doc id = Auth uid). Progreso y action logs son subcolecciones aquí.",
    fields: {
      number: "Número",
      numberDescription: "Número de cupo asignado (1, 2, 3…).",
      team: "Equipo",
      teamDescription: "Equipo del participante.",
    },
  },
  participantEconomy: {
    entityAction: "Ver puntajes y monedas",
    ariaLabel: "Economía del participante",
    title: "Puntajes y monedas",
    description: "Participante {{uid}}",
    close: "Cerrar",
    empty: "Este participante aún no tiene datos de economía.",
    error: "No se pudo cargar la economía: {{message}}",
    scoresSection: "Puntajes",
    coinsSection: "Monedas",
    individualScore: "Puntaje individual",
    teamScore: "Puntaje de equipo",
    coinsBalance: "Saldo",
    missingDoc: "Aún no creado",
    neverUpdated: "Sin fecha de actualización",
    ledgerCollectionName: "Movimientos de monedas",
    ledgerSingularName: "Movimiento de monedas",
    ledgerDescription: "Monedas ganadas, gastadas o ajustadas.",
    ledgerCreatedAt: "Creado",
    ledgerType: "Tipo",
    ledgerCoinsDelta: "Monedas (+/−)",
    ledgerLevel: "Nivel",
    ledgerTypeLevelReward: "Recompensa de nivel",
    ledgerTypeSpend: "Gasto",
    ledgerTypeAdjustment: "Ajuste",
  },
  eventInvitations: {
    collectionName: "Invitaciones",
    singularName: "Invitación",
    description:
      "Invitá por email con un rol para esta sesión. Reciben acceso al iniciar sesión con esa dirección.",
    fields: {
      email: "Email",
      emailDescription: "Deben iniciar sesión con esta dirección.",
      role: "Rol",
      status: "Estado",
      invitedBy: "Invitado por",
      emailSentAt: "Email enviado",
      emailNotSent: "Sin enviar",
      acceptedAt: "Aceptada el",
      acceptedBy: "Aceptada por",
    },
    roles: {
      participant: "Participante",
      operator: "Operador",
      presenter: "Presentador",
      manager: "Manager",
    },
    status: {
      pending: "Pendiente",
      accepted: "Aceptada",
    },
  },
  assignmentMode: {
    name: "Asignación automática",
    description:
      "Las invitaciones de staff aplican al aceptarlas en cualquier opción.",
    forEveryone: "Para todos",
    invitationOnly: "Solo con invitación",
    approvalOnly: "Solo con aprobación",
  },
  eventStatus: {
    provisioning: "Configurando",
    provisioning_failed: "Error al configurar",
    running: "Activo",
    archived: "Archivado",
    deleting: "Eliminando",
    denialProvisioningFailedWithDetail:
      "No se pudo configurar el evento: {{detail}}",
    denialProvisioningFailed:
      "No se pudo configurar el evento. Revisá el estado en la lista de eventos o contactá soporte.",
    denialProvisioning:
      "El evento se está configurando (slots y equipos). Esperá a que el estado sea Activo para abrir el Event Manager.",
    denialArchived: "Este evento está archivado.",
    denialGeneric: "Este evento no está disponible para administración.",
  },
  eventActions: {
    statusUpdated: "Estado actualizado",
    statusUpdateFailed: "No se pudo actualizar el estado",
    republishName: "Actualizar contenido",
    republishTitle: "Actualizar contenido",
    republishBody:
      "¿Generar una nueva versión del contenido desde el learning path? Los participantes necesitan conexión para descargarla; hasta entonces no podrán jugar con el contenido actualizado.",
    republishSuccess: "Contenido actualizado",
    republishQueued: "Actualización de contenido encolada",
    unarchiveName: "Desarchivar",
    unarchiveTitle: "Desarchivar evento",
    unarchiveBody:
      "¿Desarchivar este evento? Volverá a la lista principal de eventos activos.",
    archiveName: "Archivar",
    archiveTitle: "Archivar evento",
    archiveBody:
      "¿Archivar este evento? Dejará de mostrarse en la lista principal. Podés recuperarlo desde Eventos archivados.",
    deleteName: "Eliminar",
  },
  eventAccess: {
    managerDenied:
      "No tenés permiso para administrar este evento. Si necesitás acceso, pedí una invitación como administrador del evento o de la cuenta al responsable.",
    accessDeniedTitle: "Sin acceso a este evento",
    provisioningDeniedTitle: "Evento en preparación",
    backToAccount: "Volver al panel de la cuenta",
  },
  deleteEvent: {
    ariaLabel: "Eliminar evento",
    title: "Eliminar evento",
    description:
      "Vas a eliminar permanentemente el evento y todo su historial: participantes, slots, equipos, progreso, registros de acciones, leaderboards, contenido de la sesión e invitaciones asociadas. Esta acción no se puede deshacer.",
    confirmLabel: "Para confirmar, escribí exactamente:",
    cancel: "Cancelar",
    confirm: "Eliminar evento",
    queued: "Eliminación encolada",
  },
  participantProgress: {
    progressCollectionName: "Progreso",
    progressSingularName: "Progreso",
    progressDescription: "Progreso del participante por nivel.",
    unitProgressCollectionName: "Progreso de unidad",
    unitProgressSingularName: "Progreso de unidad",
    unitProgressDescription: "Progreso del participante por unidad (desbloqueo + ciclo de vida).",
    logsCollectionName: "Registros",
    logsSingularName: "Registro",
    logsDescription: "Registro cronológico de acciones del participante.",
    viewTitle: "Progreso de participantes",
    viewDescription:
      "Seleccioná un participante para ver el progreso por etapa y los registros.",
    selectParticipant: "Seleccioná un participante para ver el progreso.",
    logsSectionTitle: "Registros",
    searchPlaceholder: "Buscar participantes",
    showingLimited: "Mostrando {{shown}} de {{total}} participantes.",
    searchToFindMore: "Usá la búsqueda para encontrar otros.",
    delayColumn: "Retraso",
    durationColumn: "Duración",
  },
  actionTypes: {
    user: {
      avatar_changed: "Avatar cambiado",
      nick_changed: "Apodo cambiado",
    },
    level: {
      entered: "Entró al nivel",
      exited: "Salió del nivel",
    },
    question: {
      answered: "Pregunta respondida",
    },
    unit: {
      access_unlocked: "Acceso a unidad desbloqueado",
    },
    event: {
      content_downloaded: "Descargó el contenido del evento",
      joined: "Se unió al evento",
      left: "Salió del evento",
    },
  },
  contentInstalls: {
    viewTitle: "Instalaciones de contenido",
    viewDescription:
      "Quién tiene instalado el paquete offline publicado y cuándo lo actualizó.",
    publishedVersion: "Publicada",
    upToDate: "Al día",
    outdated: "Desactualizados",
    notInstalled: "Sin instalar",
    empty: "No hay participantes.",
    colParticipant: "Participante",
    colVersion: "Versión instalada",
    colInstalledAt: "Instalada el",
    colStatus: "Estado",
    statusUpToDate: "Al día",
    statusOutdated: "Desactualizado",
    statusNotInstalled: "Sin instalar",
    searchPlaceholder: "Buscar participantes",
    pageStatus: "Página {{page}} de {{pageCount}} ({{total}})",
    prevPage: "Anterior",
    nextPage: "Siguiente",
  },
  eventContent: {
    viewTitle: "Mapa",
    viewDescription:
      "Unidades y niveles publicados en esta sesión, con códigos de acceso y stats de progreso.",
    empty: "Esta versión publicada no tiene unidades.",
    emptyUnpublished: "Todavía no se publicó contenido para este evento.",
    unlock: "Desbloquear",
    scheduleUnlock: "Programar",
    scheduledFor: "Se abre {{when}}",
    unlocked: "Desbloqueada",
    unlockFailed: "No se pudo desbloquear esta unidad.",
    scheduleTitle: "Programar desbloqueo",
    scheduleDescription:
      "La unidad sigue cerrada hasta esa hora. Los participantes pueden entrar con el código de acceso.",
    scheduleConfirm: "Programar",
    scheduleInvalid: "Elegí una fecha y hora futuras.",
    accessCode: "Código de acceso",
    copyAccessCode: "Copiar",
    copiedAccessCode: "Copiado",
    unlockMode: "Modo de desbloqueo",
    unlockModeDescription:
      "Disponible al llegar: los participantes entran cuando llegan. Bloqueada hasta abrir: queda cerrada hasta que el evento la abra (fecha, código de acceso o Desbloquear en el admin).",
    unlockModeFree: "Disponible al llegar",
    unlockModeBlocked: "Bloqueada hasta abrir",
    noLevels: "Esta unidad no tiene niveles configurados.",
    expandUnit: "Mostrar niveles",
    collapseUnit: "Ocultar niveles",
    startedCount: "{{count}} iniciaron ({{percent}}%)",
    completedCount: "{{count}} completaron ({{percent}}%)",
    viewStats: "Ver stats",
    statsTitle: "Stats del nivel",
    statsAria: "Stats del nivel",
    close: "Cerrar",
    notStarted: "Sin empezar",
    inProgress: "En curso",
    completed: "Completado",
    percentOfParticipants: "{{percent}}%",
    avgStars: "Estrellas promedio",
    avgScore: "Puntaje promedio",
    avgDuration: "Tiempo promedio",
    starDistribution: "Distribución de estrellas",
    starsLabel: "{{stars}} estrellas",
    levelNumber: "Nivel {{order}}",
    modalities: {
      quiz: "Quiz",
      story: "Historia",
      treasureHunt: "Búsqueda del tesoro",
      towerClimb: "Torre",
      maze: "Laberinto",
      reward: "Recompensa",
    },
    difficulties: {
      normal: "Normal",
      hard: "Difícil",
      superHard: "Super difícil",
    },
    playFormats: {
      solo: "Solo",
      team: "En equipo",
    },
    computeStats: "Recalcular stats",
    computeStatsConfirmTitle: "¿Recalcular stats de niveles?",
    computeStatsConfirmBody:
      "Esto recorre el progreso de todos los participantes y puede tardar varios minutos. No se puede volver a ejecutar por unos minutos.",
    computeStatsQueued: "Recálculo de stats en cola.",
    computeStatsRunning: "Calculando stats…",
    computeStatsNever: "Todavía no se calcularon las stats.",
    computeStatsLastRun: "Último cálculo {{when}}",
    computeStatsCooldown: "Esperá {{seconds}} s para volver a calcular.",
    computeStatsFailed: "El último cálculo falló: {{error}}",
  },
  learningStats: {
    viewTitle: "Aprendizaje",
    viewDescription:
      "Qué tan bien responden las preguntas los participantes, por unidad y dificultad. Cada respuesta cuenta.",
    empty: "Esta versión publicada no tiene unidades.",
    emptyUnpublished: "Todavía no se publicó contenido para este evento.",
    close: "Cerrar",
    computeStats: "Recalcular stats",
    computeStatsConfirmTitle: "¿Recalcular stats de aprendizaje?",
    computeStatsConfirmBody:
      "Esto recorre las respuestas de cada participante y puede tardar varios minutos. No se puede volver a ejecutar por unos minutos.",
    computeStatsRunning: "Calculando stats…",
    computeStatsNever: "Todavía no se calcularon las stats.",
    computeStatsLastRun: "Último cálculo {{when}}",
    computeStatsCooldown: "Esperá {{seconds}} s antes de calcular de nuevo.",
    computeStatsFailed: "El último cálculo falló: {{error}}",
    accuracy: "Acierto",
    answered: "Respondieron",
    weakest: "Celda más débil",
    ofParticipants: "Participantes que respondieron / asignados",
    answersCount: "{{count}} respuestas",
    peopleCount: "{{count}} personas",
    easy: "Fácil",
    medium: "Medio",
    hard: "Difícil",
    unitColumn: "Unidad",
    noQuestions: "Sin preguntas",
    noAnswersInCell: "Todavía no hay respuestas en este grupo.",
    sampleTooSmall: "Menos de {{min}} respuestas — todavía no es estable",
    questionsTitle: "Preguntas",
    topWrong: "Opción más fallada: {{option}} ({{count}})",
    verdictNever: "Calculá las stats para ver cómo le va al grupo en las preguntas.",
    verdictNoAnswers: "Nadie respondió una pregunta todavía.",
    verdictTooLittle:
      "Hay respuestas, pero ninguna unidad y dificultad tiene muestra suficiente para juzgar.",
    verdictGoingWell: "Al grupo le está yendo bien en las unidades y dificultades.",
    verdictWeakest:
      "Grupo más débil: {{unit}} · {{difficulty}} · {{percent}}%.",
  },
  eventStats: {
    viewTitle: "Stats del evento",
    viewDescription:
      "Cuántos slots de participantes y equipos están registrados y activos en esta sesión.",
  },
  eventRanking: {
    individualTitle: "Ranking individual",
    individualDescription:
      "Top 100 en vivo del leaderboard del evento. Actualizar recalcula los puestos con los scores actuales.",
    teamTitle: "Ranking de equipos",
    teamDescription:
      "Ranking de equipos en vivo. Actualizar recalcula el ranking individual (el de equipos todavía no se reconstruye).",
    refresh: "Actualizar ranking",
    refreshQueued: "Actualización de ranking en cola.",
    refreshCooldown: "Esperá {{seconds}} s para volver a actualizar.",
    refreshError: "No se pudo encolar la actualización del ranking.",
    refreshing: "Actualizando ranking…",
    lastUpdated: "Última actualización {{when}}",
    lastUpdatedNever: "Todavía no se reconstruyó el ranking.",
    searchIndividual: "Buscar por nombre o número de equipo",
    searchTeam: "Buscar por número de equipo",
    emptyIndividual: "Todavía no hay ranking individual.",
    emptyTeam: "Todavía no hay ranking de equipos.",
    colRank: "#",
    colName: "Nombre",
    colTeam: "Equipo",
    colScore: "Puntaje",
    colUpdated: "Actualizado",
    colTeamNumber: "Equipo",
    colTeamScore: "Puntaje",
  },
};

export const eventManagerTranslationsPt: EventManagerTranslations = {
  assignedParticipants: {
    collectionName: "Participants",
    singularName: "Participant",
    description:
      "Jogadores nesta sessão (doc id = Auth uid). Progresso e action logs são subcoleções aqui.",
    fields: {
      number: "Número",
      numberDescription: "Número da vaga atribuída (1, 2, 3…).",
      team: "Equipe",
      teamDescription: "Equipe do participante.",
    },
  },
  participantEconomy: {
    entityAction: "Ver pontuações e moedas",
    ariaLabel: "Economia do participante",
    title: "Pontuações e moedas",
    description: "Participante {{uid}}",
    close: "Fechar",
    empty: "Este participante ainda não tem dados de economia.",
    error: "Não foi possível carregar a economia: {{message}}",
    scoresSection: "Pontuações",
    coinsSection: "Moedas",
    individualScore: "Pontuação individual",
    teamScore: "Pontuação de equipe",
    coinsBalance: "Saldo",
    missingDoc: "Ainda não criado",
    neverUpdated: "Sem data de atualização",
    ledgerCollectionName: "Movimentos de moedas",
    ledgerSingularName: "Movimento de moedas",
    ledgerDescription: "Moedas ganhas, gastas ou ajustadas.",
    ledgerCreatedAt: "Criado em",
    ledgerType: "Tipo",
    ledgerCoinsDelta: "Moedas (+/−)",
    ledgerLevel: "Nível",
    ledgerTypeLevelReward: "Recompensa de nível",
    ledgerTypeSpend: "Gasto",
    ledgerTypeAdjustment: "Ajuste",
  },
  eventInvitations: {
    collectionName: "Convites",
    singularName: "Convite",
    description:
      "Convide por email com um papel para esta sessão. O acesso é concedido ao entrar com esse endereço.",
    fields: {
      email: "Email",
      emailDescription: "Devem entrar com este endereço.",
      role: "Papel",
      status: "Estado",
      invitedBy: "Convidado por",
      emailSentAt: "Email enviado",
      emailNotSent: "Não enviado",
      acceptedAt: "Aceito em",
      acceptedBy: "Aceito por",
    },
    roles: {
      participant: "Participante",
      operator: "Operador",
      presenter: "Apresentador",
      manager: "Manager",
    },
    status: {
      pending: "Pendente",
      accepted: "Aceito",
    },
  },
  assignmentMode: {
    name: "Atribuição automática",
    description:
      "Os convites de staff aplicam-se ao serem aceitos, em qualquer opção.",
    forEveryone: "Para todos",
    invitationOnly: "Só com convite",
    approvalOnly: "Só com aprovação",
  },
  eventStatus: {
    provisioning: "Configurando",
    provisioning_failed: "Falha na configuração",
    running: "Ativo",
    archived: "Arquivado",
    deleting: "Excluindo",
    denialProvisioningFailedWithDetail:
      "Não foi possível configurar o evento: {{detail}}",
    denialProvisioningFailed:
      "Não foi possível configurar o evento. Verifique o status na lista de eventos ou contacte o suporte.",
    denialProvisioning:
      "O evento está sendo configurado (vagas e equipes). Aguarde o status Ativo para abrir o Event Manager.",
    denialArchived: "Este evento está arquivado.",
    denialGeneric: "Este evento não está disponível para administração.",
  },
  eventActions: {
    statusUpdated: "Status atualizado",
    statusUpdateFailed: "Não foi possível atualizar o status",
    republishName: "Atualizar conteúdo",
    republishTitle: "Atualizar conteúdo",
    republishBody:
      "Gerar uma nova versão do conteúdo a partir do learning path? Os participantes precisam de conexão para baixá-la; até então não poderão jogar com o conteúdo atualizado.",
    republishSuccess: "Conteúdo atualizado",
    republishQueued: "Atualização de conteúdo enfileirada",
    unarchiveName: "Desarquivar",
    unarchiveTitle: "Desarquivar evento",
    unarchiveBody:
      "Desarquivar este evento? Ele voltará à lista principal de eventos ativos.",
    archiveName: "Arquivar",
    archiveTitle: "Arquivar evento",
    archiveBody:
      "Arquivar este evento? Ele deixará de aparecer na lista principal. Você pode restaurá-lo em Eventos arquivados.",
    deleteName: "Excluir",
  },
  eventAccess: {
    managerDenied:
      "Você não tem permissão para administrar este evento. Se precisar de acesso, peça um convite como administrador do evento ou da conta ao responsável.",
    accessDeniedTitle: "Sem acesso a este evento",
    provisioningDeniedTitle: "Evento em preparação",
    backToAccount: "Voltar ao painel da conta",
  },
  deleteEvent: {
    ariaLabel: "Excluir evento",
    title: "Excluir evento",
    description:
      "Você excluirá permanentemente o evento e todo o histórico: participantes, vagas, equipes, progresso, registros de ações, leaderboards, conteúdo da sessão e convites associados. Esta ação não pode ser desfeita.",
    confirmLabel: "Para confirmar, digite exatamente:",
    cancel: "Cancelar",
    confirm: "Excluir evento",
    queued: "Exclusão enfileirada",
  },
  participantProgress: {
    progressCollectionName: "Progresso",
    progressSingularName: "Progresso",
    progressDescription: "Progresso do participante por nível.",
    unitProgressCollectionName: "Progresso da unidade",
    unitProgressSingularName: "Progresso da unidade",
    unitProgressDescription: "Progresso do participante por unidade (desbloqueio + ciclo de vida).",
    logsCollectionName: "Registros",
    logsSingularName: "Registro",
    logsDescription: "Registro cronológico de ações do participante.",
    viewTitle: "Progresso dos participantes",
    viewDescription:
      "Selecione um participante para ver o progresso por etapa e os registros.",
    selectParticipant: "Selecione um participante para ver o progresso.",
    logsSectionTitle: "Registros",
    searchPlaceholder: "Buscar participantes",
    showingLimited: "Mostrando {{shown}} de {{total}} participantes.",
    searchToFindMore: "Use a busca para encontrar outros.",
    delayColumn: "Atraso",
    durationColumn: "Duração",
  },
  actionTypes: {
    user: {
      avatar_changed: "Avatar alterado",
      nick_changed: "Apelido alterado",
    },
    level: {
      entered: "Entrou no nível",
      exited: "Saiu do nível",
    },
    question: {
      answered: "Pergunta respondida",
    },
    unit: {
      access_unlocked: "Acesso à unidade desbloqueado",
    },
    event: {
      content_downloaded: "Baixou o conteúdo do evento",
      joined: "Entrou no evento",
      left: "Saiu do evento",
    },
  },
  contentInstalls: {
    viewTitle: "Instalações de conteúdo",
    viewDescription:
      "Quem tem o pacote offline publicado instalado e quando atualizou.",
    publishedVersion: "Publicada",
    upToDate: "Atualizados",
    outdated: "Desatualizados",
    notInstalled: "Sem instalar",
    empty: "Nenhum participante encontrado.",
    colParticipant: "Participante",
    colVersion: "Versão instalada",
    colInstalledAt: "Instalada em",
    colStatus: "Estado",
    statusUpToDate: "Atualizado",
    statusOutdated: "Desatualizado",
    statusNotInstalled: "Sem instalar",
    searchPlaceholder: "Buscar participantes",
    pageStatus: "Página {{page}} de {{pageCount}} ({{total}})",
    prevPage: "Anterior",
    nextPage: "Próxima",
  },
  eventContent: {
    viewTitle: "Mapa",
    viewDescription:
      "Unidades e níveis publicados nesta sessão, com códigos de acesso e stats de conclusão.",
    empty: "Esta versão publicada não tem unidades.",
    emptyUnpublished: "Ainda não foi publicado conteúdo para este evento.",
    unlock: "Desbloquear",
    scheduleUnlock: "Agendar",
    scheduledFor: "Abre {{when}}",
    unlocked: "Desbloqueada",
    unlockFailed: "Não foi possível desbloquear esta unidade.",
    scheduleTitle: "Agendar desbloqueio",
    scheduleDescription:
      "A unidade permanece fechada até esse horário. Os participantes ainda podem entrar com o código de acesso.",
    scheduleConfirm: "Agendar",
    scheduleInvalid: "Escolha uma data e hora futuras.",
    accessCode: "Código de acesso",
    copyAccessCode: "Copiar",
    copiedAccessCode: "Copiado",
    unlockMode: "Modo de desbloqueio",
    unlockModeDescription:
      "Aberta ao chegar: os participantes entram assim que chegam. Bloqueada até abrir: permanece fechada até o evento abri-la (data, código de acesso ou Desbloquear no admin).",
    unlockModeFree: "Aberta ao chegar",
    unlockModeBlocked: "Bloqueada até abrir",
    noLevels: "Esta unidade não tem níveis configurados.",
    expandUnit: "Mostrar níveis",
    collapseUnit: "Ocultar níveis",
    startedCount: "{{count}} começaram ({{percent}}%)",
    completedCount: "{{count}} concluíram ({{percent}}%)",
    viewStats: "Ver stats",
    statsTitle: "Stats do nível",
    statsAria: "Stats do nível",
    close: "Fechar",
    notStarted: "Não iniciado",
    inProgress: "Em andamento",
    completed: "Concluído",
    percentOfParticipants: "{{percent}}%",
    avgStars: "Média de estrelas",
    avgScore: "Pontuação média",
    avgDuration: "Tempo médio",
    starDistribution: "Distribuição de estrelas",
    starsLabel: "{{stars}} estrelas",
    levelNumber: "Nível {{order}}",
    modalities: {
      quiz: "Quiz",
      story: "História",
      treasureHunt: "Caça ao tesouro",
      towerClimb: "Torre",
      maze: "Labirinto",
      reward: "Recompensa",
    },
    difficulties: {
      normal: "Normal",
      hard: "Difícil",
      superHard: "Super difícil",
    },
    playFormats: {
      solo: "Solo",
      team: "Em equipe",
    },
    computeStats: "Recalcular stats",
    computeStatsConfirmTitle: "Recalcular stats dos níveis?",
    computeStatsConfirmBody:
      "Isso percorre o progresso de todos os participantes e pode levar vários minutos. Não é possível executar de novo por alguns minutos.",
    computeStatsQueued: "Recálculo de stats na fila.",
    computeStatsRunning: "Calculando stats…",
    computeStatsNever: "As stats ainda não foram calculadas.",
    computeStatsLastRun: "Último cálculo {{when}}",
    computeStatsCooldown: "Aguarde {{seconds}} s para calcular de novo.",
    computeStatsFailed: "O último cálculo falhou: {{error}}",
  },
  learningStats: {
    viewTitle: "Aprendizagem",
    viewDescription:
      "Como os participantes estão respondendo às perguntas, por unidade e dificuldade. Cada resposta conta.",
    empty: "Esta versão publicada não tem unidades.",
    emptyUnpublished: "Ainda não foi publicado conteúdo para este evento.",
    close: "Fechar",
    computeStats: "Recalcular stats",
    computeStatsConfirmTitle: "Recalcular stats de aprendizagem?",
    computeStatsConfirmBody:
      "Isso percorre as respostas de cada participante e pode levar vários minutos. Não é possível executar de novo por alguns minutos.",
    computeStatsRunning: "Calculando stats…",
    computeStatsNever: "As stats ainda não foram calculadas.",
    computeStatsLastRun: "Último cálculo {{when}}",
    computeStatsCooldown: "Espere {{seconds}} s antes de calcular de novo.",
    computeStatsFailed: "O último cálculo falhou: {{error}}",
    accuracy: "Acerto",
    answered: "Responderam",
    weakest: "Célula mais fraca",
    ofParticipants: "Participantes que responderam / atribuídos",
    answersCount: "{{count}} respostas",
    peopleCount: "{{count}} pessoas",
    easy: "Fácil",
    medium: "Médio",
    hard: "Difícil",
    unitColumn: "Unidade",
    noQuestions: "Sem perguntas",
    noAnswersInCell: "Ainda não há respostas neste grupo.",
    sampleTooSmall: "Menos de {{min}} respostas — ainda não é estável",
    questionsTitle: "Perguntas",
    topWrong: "Opção mais errada: {{option}} ({{count}})",
    verdictNever: "Calcule as stats para ver como o grupo está nas perguntas.",
    verdictNoAnswers: "Ninguém respondeu a uma pergunta ainda.",
    verdictTooLittle:
      "Há respostas, mas nenhuma unidade e dificuldade tem amostra suficiente para julgar.",
    verdictGoingWell: "O grupo está bem nas unidades e dificuldades.",
    verdictWeakest:
      "Grupo mais fraco: {{unit}} · {{difficulty}} · {{percent}}%.",
  },
  eventStats: {
    viewTitle: "Stats do evento",
    viewDescription:
      "Quantos slots de participantes e equipes estão registrados e ativos nesta sessão.",
  },
  eventRanking: {
    individualTitle: "Ranking individual",
    individualDescription:
      "Top 100 ao vivo do leaderboard do evento. Atualizar recalcula as posições com os scores atuais.",
    teamTitle: "Ranking de equipes",
    teamDescription:
      "Ranking de equipes ao vivo. Atualizar recalcula o ranking individual (o de equipes ainda não é reconstruído).",
    refresh: "Atualizar ranking",
    refreshQueued: "Atualização de ranking na fila.",
    refreshCooldown: "Aguarde {{seconds}} s para atualizar de novo.",
    refreshError: "Não foi possível enfileirar a atualização do ranking.",
    refreshing: "Atualizando ranking…",
    lastUpdated: "Última atualização {{when}}",
    lastUpdatedNever: "O ranking ainda não foi reconstruído.",
    searchIndividual: "Buscar por nome ou número da equipe",
    searchTeam: "Buscar por número da equipe",
    emptyIndividual: "Ainda não há ranking individual.",
    emptyTeam: "Ainda não há ranking de equipes.",
    colRank: "#",
    colName: "Nome",
    colTeam: "Equipe",
    colScore: "Pontuação",
    colUpdated: "Atualizado",
    colTeamNumber: "Equipe",
    colTeamScore: "Pontuação",
  },
};

export const eventManagerTranslationsFr: EventManagerTranslations = {
  assignedParticipants: {
    collectionName: "Participants",
    singularName: "Participant",
    description:
      "Joueurs de cette session (doc id = Auth uid). Progression et journaux d'actions en sous-collections.",
    fields: {
      number: "Numéro",
      numberDescription: "Numéro de place attribué (1, 2, 3…).",
      team: "Équipe",
      teamDescription: "Équipe du participant.",
    },
  },
  participantEconomy: {
    entityAction: "Voir scores et pièces",
    ariaLabel: "Économie du participant",
    title: "Scores et pièces",
    description: "Participant {{uid}}",
    close: "Fermer",
    empty: "Ce participant n'a pas encore de données d'économie.",
    error: "Impossible de charger l'économie : {{message}}",
    scoresSection: "Scores",
    coinsSection: "Pièces",
    individualScore: "Score individuel",
    teamScore: "Score d'équipe",
    coinsBalance: "Solde",
    missingDoc: "Pas encore créé",
    neverUpdated: "Sans date de mise à jour",
    ledgerCollectionName: "Mouvements de pièces",
    ledgerSingularName: "Mouvement de pièces",
    ledgerDescription: "Pièces gagnées, dépensées ou ajustées.",
    ledgerCreatedAt: "Créé le",
    ledgerType: "Type",
    ledgerCoinsDelta: "Pièces (+/−)",
    ledgerLevel: "Niveau",
    ledgerTypeLevelReward: "Récompense de niveau",
    ledgerTypeSpend: "Dépense",
    ledgerTypeAdjustment: "Ajustement",
  },
  eventInvitations: {
    collectionName: "Invitations",
    singularName: "Invitation",
    description:
      "Invitez par email avec un rôle pour cette session. L'accès est accordé à la connexion avec cette adresse.",
    fields: {
      email: "Email",
      emailDescription: "Ils doivent se connecter avec cette adresse.",
      role: "Rôle",
      status: "Statut",
      invitedBy: "Invité par",
      emailSentAt: "Email envoyé",
      emailNotSent: "Non envoyé",
      acceptedAt: "Acceptée le",
      acceptedBy: "Acceptée par",
    },
    roles: {
      participant: "Participant",
      operator: "Opérateur",
      presenter: "Présentateur",
      manager: "Manager",
    },
    status: {
      pending: "En attente",
      accepted: "Acceptée",
    },
  },
  assignmentMode: {
    name: "Affectation automatique",
    description:
      "Les invitations du staff s'appliquent dès leur acceptation, quelle que soit l'option.",
    forEveryone: "Pour tout le monde",
    invitationOnly: "Sur invitation uniquement",
    approvalOnly: "Sur approbation uniquement",
  },
  eventStatus: {
    provisioning: "Configuration",
    provisioning_failed: "Échec de configuration",
    running: "Actif",
    archived: "Archivé",
    deleting: "Suppression",
    denialProvisioningFailedWithDetail:
      "Impossible de configurer l'événement : {{detail}}",
    denialProvisioningFailed:
      "Impossible de configurer l'événement. Vérifiez le statut dans la liste des événements ou contactez le support.",
    denialProvisioning:
      "L'événement est en cours de configuration (places et équipes). Attendez le statut Actif pour ouvrir l'Event Manager.",
    denialArchived: "Cet événement est archivé.",
    denialGeneric: "Cet événement n'est pas disponible pour l'administration.",
  },
  eventActions: {
    statusUpdated: "Statut mis à jour",
    statusUpdateFailed: "Impossible de mettre à jour le statut",
    republishName: "Mettre à jour le contenu",
    republishTitle: "Mettre à jour le contenu",
    republishBody:
      "Générer une nouvelle version du contenu depuis le learning path ? Les participants ont besoin d'une connexion pour la télécharger ; jusqu'alors ils ne pourront pas jouer avec le contenu mis à jour.",
    republishSuccess: "Contenu mis à jour",
    republishQueued: "Mise à jour du contenu mise en file",
    unarchiveName: "Désarchiver",
    unarchiveTitle: "Désarchiver l'événement",
    unarchiveBody:
      "Désarchiver cet événement ? Il reviendra dans la liste principale des événements actifs.",
    archiveName: "Archiver",
    archiveTitle: "Archiver l'événement",
    archiveBody:
      "Archiver cet événement ? Il n'apparaîtra plus dans la liste principale. Vous pourrez le restaurer depuis Événements archivés.",
    deleteName: "Supprimer",
  },
  eventAccess: {
    managerDenied:
      "Vous n'avez pas la permission d'administrer cet événement. Si vous avez besoin d'accès, demandez une invitation comme administrateur d'événement ou de compte au responsable.",
    accessDeniedTitle: "Pas d'accès à cet événement",
    provisioningDeniedTitle: "Événement en préparation",
    backToAccount: "Retour au panneau du compte",
  },
  deleteEvent: {
    ariaLabel: "Supprimer l'événement",
    title: "Supprimer l'événement",
    description:
      "Vous supprimerez définitivement l'événement et tout son historique : participants, places, équipes, progression, journaux d'actions, classements, contenu de session et invitations associées. Cette action est irréversible.",
    confirmLabel: "Pour confirmer, saisissez exactement :",
    cancel: "Annuler",
    confirm: "Supprimer l'événement",
    queued: "Suppression en file d'attente",
  },
  participantProgress: {
    progressCollectionName: "Progression",
    progressSingularName: "Progression",
    progressDescription: "Progression du participant par niveau.",
    unitProgressCollectionName: "Progression d'unité",
    unitProgressSingularName: "Progression d'unité",
    unitProgressDescription: "Progression du participant par unité (déverrouillage + cycle de vie).",
    logsCollectionName: "Journaux",
    logsSingularName: "Entrée",
    logsDescription: "Enregistrement chronologique des actions du participant.",
    viewTitle: "Progression des participants",
    viewDescription:
      "Sélectionnez un participant pour voir la progression par étape et les journaux.",
    selectParticipant: "Sélectionnez un participant pour voir la progression.",
    logsSectionTitle: "Journaux",
    searchPlaceholder: "Rechercher des participants",
    showingLimited: "Affichage de {{shown}} sur {{total}} participants.",
    searchToFindMore: "Utilisez la recherche pour en trouver d'autres.",
    delayColumn: "Délai",
    durationColumn: "Durée",
  },
  actionTypes: {
    user: {
      avatar_changed: "Avatar modifié",
      nick_changed: "Pseudo modifié",
    },
    level: {
      entered: "Entrée dans le niveau",
      exited: "Sortie du niveau",
    },
    question: {
      answered: "Question répondue",
    },
    unit: {
      access_unlocked: "Accès à l'unité déverrouillé",
    },
    event: {
      content_downloaded: "Contenu de l'événement téléchargé",
      joined: "A rejoint l'événement",
      left: "A quitté l'événement",
    },
  },
  contentInstalls: {
    viewTitle: "Installations de contenu",
    viewDescription:
      "Qui a le paquet hors ligne publié installé, et quand il a été mis à jour.",
    publishedVersion: "Publiée",
    upToDate: "À jour",
    outdated: "Obsolètes",
    notInstalled: "Non installé",
    empty: "Aucun participant trouvé.",
    colParticipant: "Participant",
    colVersion: "Version installée",
    colInstalledAt: "Installée le",
    colStatus: "Statut",
    statusUpToDate: "À jour",
    statusOutdated: "Obsolète",
    statusNotInstalled: "Non installé",
    searchPlaceholder: "Rechercher des participants",
    pageStatus: "Page {{page}} sur {{pageCount}} ({{total}})",
    prevPage: "Précédent",
    nextPage: "Suivant",
  },
  eventContent: {
    viewTitle: "Carte",
    viewDescription:
      "Unités et niveaux publiés pour cette session, avec codes d'accès et stats de complétion.",
    empty: "Cette version publiée n'a aucune unité.",
    emptyUnpublished: "Aucun contenu n'a encore été publié pour cet événement.",
    unlock: "Déverrouiller",
    scheduleUnlock: "Planifier",
    scheduledFor: "S'ouvre {{when}}",
    unlocked: "Déverrouillée",
    unlockFailed: "Impossible de déverrouiller cette unité.",
    scheduleTitle: "Planifier le déverrouillage",
    scheduleDescription:
      "L'unité reste fermée jusqu'à cette heure. Les participants peuvent toujours entrer avec le code d'accès.",
    scheduleConfirm: "Planifier",
    scheduleInvalid: "Choisissez une date et une heure dans le futur.",
    accessCode: "Code d'accès",
    copyAccessCode: "Copier",
    copiedAccessCode: "Copié",
    unlockMode: "Mode de déverrouillage",
    unlockModeDescription:
      "Ouverte à l'arrivée : les participants y entrent dès qu'ils l'atteignent. Verrouillée jusqu'à ouverture : reste fermée jusqu'à ce que l'événement l'ouvre (date, code d'accès ou Déverrouiller dans l'admin).",
    unlockModeFree: "Ouverte à l'arrivée",
    unlockModeBlocked: "Verrouillée jusqu'à ouverture",
    noLevels: "Aucun niveau configuré pour cette unité.",
    expandUnit: "Afficher les niveaux",
    collapseUnit: "Masquer les niveaux",
    startedCount: "{{count}} commencés ({{percent}} %)",
    completedCount: "{{count}} terminés ({{percent}} %)",
    viewStats: "Voir les stats",
    statsTitle: "Stats du niveau",
    statsAria: "Stats du niveau",
    close: "Fermer",
    notStarted: "Non commencé",
    inProgress: "En cours",
    completed: "Terminé",
    percentOfParticipants: "{{percent}} %",
    avgStars: "Étoiles moyennes",
    avgScore: "Score moyen",
    avgDuration: "Temps moyen",
    starDistribution: "Répartition des étoiles",
    starsLabel: "{{stars}} étoiles",
    levelNumber: "Niveau {{order}}",
    modalities: {
      quiz: "Quiz",
      story: "Histoire",
      treasureHunt: "Chasse au trésor",
      towerClimb: "Tour",
      maze: "Labyrinthe",
      reward: "Récompense",
    },
    difficulties: {
      normal: "Normal",
      hard: "Difficile",
      superHard: "Très difficile",
    },
    playFormats: {
      solo: "Solo",
      team: "En équipe",
    },
    computeStats: "Recalculer les stats",
    computeStatsConfirmTitle: "Recalculer les stats des niveaux ?",
    computeStatsConfirmBody:
      "Cela parcourt la progression de tous les participants et peut prendre plusieurs minutes. Vous ne pourrez pas relancer avant quelques minutes.",
    computeStatsQueued: "Recalcul des stats mis en file.",
    computeStatsRunning: "Calcul des stats…",
    computeStatsNever: "Les stats n'ont pas encore été calculées.",
    computeStatsLastRun: "Dernier calcul {{when}}",
    computeStatsCooldown: "Attendez {{seconds}} s avant de recalculer.",
    computeStatsFailed: "Le dernier calcul a échoué : {{error}}",
  },
  learningStats: {
    viewTitle: "Apprentissage",
    viewDescription:
      "Comment les participants réussissent les questions, par unité et difficulté. Chaque réponse compte.",
    empty: "Cette version publiée n'a aucune unité.",
    emptyUnpublished: "Aucun contenu n'a encore été publié pour cet événement.",
    close: "Fermer",
    computeStats: "Recalculer les stats",
    computeStatsConfirmTitle: "Recalculer les stats d'apprentissage ?",
    computeStatsConfirmBody:
      "Cela parcourt les réponses de chaque participant et peut prendre plusieurs minutes. Vous ne pourrez pas relancer avant quelques minutes.",
    computeStatsRunning: "Calcul des stats…",
    computeStatsNever: "Les stats n'ont pas encore été calculées.",
    computeStatsLastRun: "Dernier calcul {{when}}",
    computeStatsCooldown: "Attendez {{seconds}} s avant de recalculer.",
    computeStatsFailed: "Le dernier calcul a échoué : {{error}}",
    accuracy: "Réussite",
    answered: "Ont répondu",
    weakest: "Case la plus faible",
    ofParticipants: "Participants ayant répondu / assignés",
    answersCount: "{{count}} réponses",
    peopleCount: "{{count}} personnes",
    easy: "Facile",
    medium: "Moyen",
    hard: "Difficile",
    unitColumn: "Unité",
    noQuestions: "Aucune question",
    noAnswersInCell: "Pas encore de réponses dans ce groupe.",
    sampleTooSmall: "Moins de {{min}} réponses — pas encore stable",
    questionsTitle: "Questions",
    topWrong: "Option la plus manquée : {{option}} ({{count}})",
    verdictNever: "Calculez les stats pour voir comment le groupe réussit les questions.",
    verdictNoAnswers: "Personne n'a encore répondu à une question.",
    verdictTooLittle:
      "Il y a des réponses, mais aucune unité et difficulté n'a assez d'échantillon pour juger.",
    verdictGoingWell: "Le groupe réussit bien sur les unités et difficultés.",
    verdictWeakest:
      "Groupe le plus faible : {{unit}} · {{difficulty}} · {{percent}} %.",
  },
  eventStats: {
    viewTitle: "Stats de l'événement",
    viewDescription:
      "Combien de slots de participants et d'équipes sont inscrits et actifs dans cette session.",
  },
  eventRanking: {
    individualTitle: "Classement individuel",
    individualDescription:
      "Top 100 en direct du classement de l'événement. Mettre à jour recalcule les places avec les scores actuels.",
    teamTitle: "Classement des équipes",
    teamDescription:
      "Classement des équipes en direct. Mettre à jour recalcule le classement individuel (celui des équipes n'est pas encore reconstruit).",
    refresh: "Mettre à jour le classement",
    refreshQueued: "Mise à jour du classement mise en file.",
    refreshCooldown: "Attendez {{seconds}} s avant de mettre à jour à nouveau.",
    refreshError: "Impossible de mettre en file la mise à jour du classement.",
    refreshing: "Mise à jour du classement…",
    lastUpdated: "Dernière mise à jour {{when}}",
    lastUpdatedNever: "Le classement n'a pas encore été reconstruit.",
    searchIndividual: "Rechercher par nom ou numéro d'équipe",
    searchTeam: "Rechercher par numéro d'équipe",
    emptyIndividual: "Pas encore de classement individuel.",
    emptyTeam: "Pas encore de classement d'équipes.",
    colRank: "#",
    colName: "Nom",
    colTeam: "Équipe",
    colScore: "Score",
    colUpdated: "Mis à jour",
    colTeamNumber: "Équipe",
    colTeamScore: "Score",
  },
};
